import { extractPathname } from '@/utils/extractPathname';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

/**
 * Key used to remember the last non-`/settings/*` route the user was on.
 * Read by `useSettingsRouteRedirect` to send users back to a meaningful page
 * when the legacy `/settings/<section>` route is intercepted by the modal.
 */
export const PREVIOUS_NON_SETTINGS_ROUTE_KEY = 'heidi.lastNonSettingsRoute';

/**
 * Removes a leading `/<locale>/…` segment when it matches one of Next's
 * configured locales. Next emits `routeChangeStart` URLs with the locale
 * prefix attached (e.g. `/en/settings/heidi-labs`); `router.asPath` does not.
 * Normalising both inputs keeps the `/settings` filter and the value we hand
 * back to `router.replace` consistent regardless of which source they came
 * from.
 *
 * `defaultLocale` is excluded from stripping. Next never emits the
 * default-locale prefix in URLs (that's the whole point of being the
 * default), but `router.locales` includes it — so without this guard a real
 * page like `/default/<route>` would be incorrectly mangled to `/<route>`.
 */
const stripLocalePrefix = (
  url: string,
  locales: readonly string[],
  defaultLocale: string | undefined
): string => {
  if (!url.startsWith('/')) return url;
  const slashIndex = url.indexOf('/', 1);
  const head = slashIndex === -1 ? url.slice(1) : url.slice(1, slashIndex);
  if (head === defaultLocale) return url;
  if (!locales.includes(head)) return url;
  return slashIndex === -1 ? '/' : url.slice(slashIndex);
};

/**
 * Default destination when no previous route has been captured (e.g. user
 * hard-loaded `/settings/account` as their first page in the session).
 */
export const PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK = '/';

/**
 * Recording one of these as the "go back to" route loops the modal redirect:
 * `/integrations` (+ `/<slug>`) are deprecated trampolines into
 * `/settings/integrations`, and `/register` sends an authenticated user
 * straight back to `/settings/billing`.
 */
export const isUnsafeGoBackRoute = (pathname: string): boolean =>
  pathname.startsWith('/settings') ||
  pathname.startsWith('/letterhead-editor') ||
  pathname === '/register' ||
  pathname === '/integrations' ||
  pathname.startsWith('/integrations/');

/**
 * Tracks the user's most recent non-`/settings/*` page in `sessionStorage`.
 *
 * Mounted once globally (in `GlobalModalsWrapper`) so a `/settings/*` deep
 * link always has a meaningful "go back to" route to redirect onto.
 */
export const useTrackPreviousSettingsRoute = () => {
  const router = useRouter();
  const initialisedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Mount-time seed: capture the page the user landed on as their
    // "previous non-settings route" so the legacy redirect has a real
    // destination on the very first navigation away. Skipped if the
    // initial page already starts with `/settings` — we deliberately
    // do not record settings routes as the "go back to" destination,
    // since redirecting there would loop back into the modal flow.
    //
    // Edge case: hard-loading `/settings/<section>` as the very first
    // page means no seed runs and `routeChangeStart` won't fire until
    // the user navigates somewhere. Until then,
    // `readPreviousSettingsRoute()` returns
    // `PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK` (`/`). That is intentional
    // — see the constant's JSDoc and design-doc §1 (Hard-load behaviour
    // table) — and it's the same destination the user would have hit
    // by clicking the brand link. No action needed.
    if (!initialisedRef.current && !isUnsafeGoBackRoute(router.pathname)) {
      try {
        sessionStorage.setItem(PREVIOUS_NON_SETTINGS_ROUTE_KEY, router.asPath);
      } catch {
        // sessionStorage can throw in private mode / when full; tolerate.
      }
      initialisedRef.current = true;
    }

    const handle = (url: string) => {
      // Next emits `routeChangeStart` URLs with the locale prefix attached
      // (e.g. `/en/settings/heidi-labs`); without stripping it, the
      // `/settings` filter would let those through and we'd record the
      // very page the user is trying to be redirected away from. Storing
      // the stripped form also means the value matches `router.asPath`
      // (which is locale-stripped), so callers can treat both sources
      // interchangeably.
      const normalised = stripLocalePrefix(
        url,
        router.locales ?? [],
        router.defaultLocale
      );
      const pathname = extractPathname(normalised, normalised);
      if (isUnsafeGoBackRoute(pathname)) {
        return;
      }
      try {
        sessionStorage.setItem(PREVIOUS_NON_SETTINGS_ROUTE_KEY, normalised);
      } catch {
        // see above
      }
    };

    router.events.on('routeChangeStart', handle);
    return () => {
      router.events.off('routeChangeStart', handle);
    };
  }, [router]);
};

export const readPreviousSettingsRoute = (): string => {
  if (typeof window === 'undefined') return PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK;
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(PREVIOUS_NON_SETTINGS_ROUTE_KEY);
  } catch {
    return PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK;
  }
  if (!stored) return PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK;
  // A value stored before the tracker excluded these routes (an already-open
  // tab) would redirect back into settings and loop — never hand one back.
  if (isUnsafeGoBackRoute(extractPathname(stored))) {
    return PREVIOUS_NON_SETTINGS_ROUTE_FALLBACK;
  }
  return stored;
};
